import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import Select from 'react-select';
import validator from 'validator';

export default function StaffRegister() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    phone: '',
    password: '',
    address: '',
    group: null  // теперь храним ID группы
  });
  const [instructorId, setInstructorId] = useState('');
  const [groups, setGroups] = useState([]);
  const [masters, setMasters] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Загрузка групп
    fetch('/api/groups')
      .then(res => res.json())
      .then(data => {
        console.log('📥 Загруженные группы:', data);
        // Преобразуем для react-select
        const groupOptions = data.map(group => ({
          value: group.id,           // ID группы (число)
          label: group.name,          // Название группы для отображения
          theoryDays: group.theoryDays,
          theoryTime: group.theoryTime,
          theoryDuration: group.theoryDuration
        }));
        setGroups(groupOptions);
      })
      .catch(err => {
        console.error('Ошибка загрузки групп:', err);
        setGroups([]);
      });

    // Загрузка инструкторов
    fetch('/api/masters', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => res.json())
      .then(data => {
        console.log('📥 Загруженные инструкторы:', data);
        setMasters(data);
      })
      .catch(err => {
        console.error('Ошибка загрузки инструкторов:', err);
        setMasters([]);
      });
  }, []);

  if (user?.status !== 'staff') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center py-20 text-red-600 text-2xl">
          Доступ запрещён
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    // Получаем выбранную группу
    const selectedGroup = groups.find(g => g.value === form.group);
    
    const submitForm = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      middleName: form.middleName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      password: form.password.trim(),
      address: form.address.trim(),
      group: form.group, // ✅ ID группы (число), а не название
      instructorId: instructorId || null
    };

    console.log('📤 Отправка формы:', submitForm);

    // Клиентская валидация
    if (!submitForm.firstName || !submitForm.lastName || !submitForm.email || !submitForm.phone || !submitForm.password || !submitForm.group) {
      setMessage('Заполните все обязательные поля');
      setLoading(false);
      return;
    }
    
    if (!validator.isEmail(submitForm.email)) {
      setMessage('Неверный email');
      setLoading(false);
      return;
    }
    
    if (!validator.isMobilePhone(submitForm.phone, 'ru-RU')) {
      setMessage('Неверный телефон. Формат: +79999999999');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/staff-register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submitForm)
      });

      const data = await res.json();
      
      if (res.ok) {
        setMessage('✅ Ученик успешно зарегистрирован');
        setForm({
          firstName: '',
          lastName: '',
          middleName: '',
          email: '',
          phone: '',
          password: '',
          address: '',
          group: null
        });
        setInstructorId('');
      } else {
        setMessage(data.message || '❌ Ошибка регистрации');
      }
    } catch (err) {
      console.error('Ошибка:', err);
      setMessage('❌ Ошибка сервера');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <Header />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Заголовок */}
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4 text-gray-900">
              Регистрация ученика
            </h1>
            <p className="text-gray-600 text-lg">
              Заполните все обязательные поля для создания нового ученика
            </p>
          </div>

          {/* Форма */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* ФИО */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Имя <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition text-gray-900"
                    placeholder="Иван"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Фамилия <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition text-gray-900"
                    placeholder="Иванов"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Отчество
                  </label>
                  <input
                    type="text"
                    value={form.middleName}
                    onChange={(e) => setForm({ ...form, middleName: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition text-gray-900"
                    placeholder="Иванович"
                  />
                </div>
              </div>

              {/* Email и телефон */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition text-gray-900"
                    placeholder="ivan@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Телефон <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition text-gray-900"
                    placeholder="+79991234567"
                    required
                  />
                </div>
              </div>

              {/* Пароль и адрес */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Пароль <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition text-gray-900"
                    placeholder="••••••••"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Адрес проживания
                  </label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition text-gray-900"
                    placeholder="г. Москва, ул. Ленина, д. 1"
                  />
                </div>
              </div>

              {/* Выбор группы */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Группа обучения <span className="text-red-500">*</span>
                </label>
                <Select
                  value={groups.find(g => g.value === form.group) || null}
                  onChange={(selected) => {
                    setForm({ ...form, group: selected ? selected.value : null });
                    console.log('Выбрана группа ID:', selected?.value);
                  }}
                  options={groups}
                  placeholder="Выберите группу..."
                  classNamePrefix="select"
                  isClearable
                  required
                  formatOptionLabel={({ label, theoryDays, theoryTime, theoryDuration }) => (
                    <div className="flex flex-col py-1">
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-xs text-gray-600">
                        {theoryDays?.join(', ')} • {theoryTime} ({theoryDuration}ч)
                      </div>
                    </div>
                  )}
                  styles={{
                    control: (base) => ({
                      ...base,
                      padding: '2px',
                      borderWidth: '2px',
                      borderRadius: '12px',
                      '&:hover': { borderColor: '#3b82f6' }
                    }),
                    option: (base) => ({
                      ...base,
                      color: '#111827'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#111827'
                    })
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Выберите группу для расписания теоретических занятий
                </p>
              </div>

              {/* Выбор инструктора */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Инструктор
                </label>
                <Select
                  value={masters.find(m => m.id === instructorId) 
                    ? { value: instructorId, label: masters.find(m => m.id === instructorId)?.fullName }
                    : null
                  }
                  onChange={(selected) => setInstructorId(selected ? selected.value : '')}
                  options={masters.map(m => ({ 
                    value: m.id, 
                    label: m.fullName,
                    photo: m.photoUrl,
                    experience: m.experience
                  }))}
                  formatOptionLabel={({ label, photo, experience }) => (
                    <div className="flex items-center">
                      {photo ? (
                        <img 
                          src={photo} 
                          alt={label} 
                          className="w-8 h-8 rounded-full mr-3 object-cover border-2 border-gray-200" 
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full mr-3 flex items-center justify-center text-white text-sm font-bold">
                          {label?.charAt(0) || '?'}
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{label}</span>
                        {experience && (
                          <span className="text-xs text-gray-600">Стаж: {experience} лет</span>
                        )}
                      </div>
                    </div>
                  )}
                  placeholder="Выберите инструктора (необязательно)"
                  classNamePrefix="select"
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      padding: '2px',
                      borderWidth: '2px',
                      borderRadius: '12px',
                      '&:hover': { borderColor: '#3b82f6' }
                    }),
                    option: (base) => ({
                      ...base,
                      color: '#111827'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#111827'
                    })
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Можно назначить позже в профиле ученика
                </p>
              </div>

              {/* Кнопка отправки */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 transition-all transform hover:scale-[1.02] shadow-lg"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Регистрация...
                  </span>
                ) : 'Зарегистрировать ученика'}
              </button>
            </form>

            {/* Сообщение */}
            {message && (
              <div className={`mt-6 p-4 rounded-xl text-center font-medium ${
                message.includes('успешно') 
                  ? 'bg-green-100 text-green-700 border border-green-200' 
                  : 'bg-red-100 text-red-700 border border-red-200'
              }`}>
                {message}
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}